import type { FieldValues, Path, ResetFieldConfig } from 'react-hook-form';

import { useFormContext } from 'react-hook-form';

/**
 * Custom hook that returns a function to reset specified fields in a form.
 *
 * @returns A function that resets the specified fields in the form.
 */
function useResetFields<TFieldValues extends FieldValues>(): (
    fieldsToUpdate: Array<Path<TFieldValues>>,
    shouldTriggerValidation?: boolean,
    resetOptions?: ResetFieldConfig<TFieldValues, Path<TFieldValues>>
) => void {
    const { trigger, resetField } = useFormContext<TFieldValues>();

    /**
     * Resets the specified fields in the form.
     *
     * @param fieldsToUpdate - An array of field paths to reset.
     * @param shouldTriggerValidation - Optional. Determines whether to trigger validation after resetting the fields. Defaults to true.
     * @param resetOptions - Optional reset configuration for the fields.
     */
    return (
        fieldsToUpdate: Array<Path<TFieldValues>>,
        shouldTriggerValidation = true,
        resetOptions?: ResetFieldConfig<TFieldValues, Path<TFieldValues>>
    ): void => {
        fieldsToUpdate.forEach((fieldPath) => {
            resetField(fieldPath, resetOptions);

            if (shouldTriggerValidation) {
                void trigger(fieldPath);
            }
        });
    };
}

export { useResetFields };
